import { setPageMeta, SITE_ORIGIN } from "@/lib/seo";

// Default fallback image (used when no specific image is mapped).
const DEFAULT_IMAGE = "/images/jtg-logo-preview.png";

/**
 * Per-route SEO registry.
 *
 * Order matters for prefix matching — list longer/more-specific paths
 * before shorter ones. We try an exact match first, then a prefix match.
 *
 * Pages that already call setPageMeta in their own useEffect will
 * override these values (last write wins), which is the desired
 * behaviour for blog articles + city pages that need dynamic data.
 */

export interface RouteSeoEntry {
  /** Exact path or path prefix this entry applies to. */
  match: string;
  /** "exact" → full string equality. "prefix" → startsWith. */
  mode?: "exact" | "prefix";
  title: string;
  description: string;
  image?: string;
  type?: string;
  imageAlt?: string;
  /** Optional canonical override (defaults to the matched path). */
  canonical?: string;
}

// We can't import bundled assets here without forcing every chunk to pull
// them in. Use static paths under /images/ (publicly served) for fallbacks
// and let page-level setPageMeta calls supply hashed asset URLs for the
// trailer-specific images they already import.

export const ROUTE_SEO: RouteSeoEntry[] = [
  {
    match: "/trailer-payload-calculator",
    mode: "exact",
    title: 'Trailer Payload & GVWR Calculator: Yards, Towing & CDL Check',
    description:
      "Free calculator: see your trailer's real payload, how many yards of dirt, gravel or debris you can haul, whether your truck can tow it, and if you're near CDL limits.",
    type: "website",
  },
  {
    match: "/",
    mode: "exact",
    title: 'Trailer Education, Towing & Maintenance Guides | Juan The Trailer Guy',
    description:
      'Your resource for trailer education, towing requirements, maintenance tips, and practical industry insights. Learn what matters before buying, towing, or maintaining a trailer.',
    image: "/images/jtg-social.jpg",
    type: "website",
  },
  {
    match: "/privacy-policy",
    mode: "exact",
    title: 'Privacy Policy | Juan The Trailer Guy',
    description:
      'How JuanTheTrailerGuy.com handles contact messages, cookies and analytics data.',
    image: "/images/jtg-logo-preview.png",
    type: "website",
  },
  {
    match: "/terms",
    mode: "exact",
    title: 'Terms of Use | Juan The Trailer Guy',
    description:
      'Terms of use for JuanTheTrailerGuy.com, an independent trailer education website.',
    image: "/images/jtg-logo-preview.png",
    type: "website",
  },
  {
    match: "/cookie-policy",
    mode: "exact",
    title: 'Cookie Policy | Juan The Trailer Guy',
    description:
      'How JuanTheTrailerGuy.com uses cookies for analytics.',
    image: "/images/jtg-logo-preview.png",
    type: "website",
  },
  {
    match: "/contact",
    mode: "exact",
    title: 'Contact Juan The Trailer Guy | Questions, Feedback & Media',
    description:
      'Have a question about trailer education, feedback on an article, or a media request? Send Juan The Trailer Guy a message.',
    type: "website",
  },
  {
    match: "/about",
    mode: "exact",
    title: 'About Juan Hernandez | Independent Trailer Educator',
    description:
      'Juan Hernandez shares 7+ years of trailer industry experience: configurations, GVWR and CDL rules, towing setups and maintenance, in plain English and Spanish.',
    image: "/images/juan-about.jpg",
    type: "profile",
  },
  {
    match: "/build-your-flatbed",
    mode: "exact",
    title: 'Flatbed Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to flatbed trailer options: gooseneck vs bumper pull, deck lengths, GVWR, ramps and tires. Learn what each choice does and when it matters.',
    image: "/images/social/flatbed.jpg",
    type: "article",
  },
  {
    match: "/build-your-roll-off",
    mode: "prefix",
    title: 'Roll-Off Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to roll-off trailer options: hooklift vs cable pull, rail width, GVWR and container sizes. Learn what each choice does and when it matters.',
    image: "/images/social/rolloff.jpg",
    type: "article",
  },
  {
    match: "/build-your-gravity-tilt",
    mode: "exact",
    title: 'Gravity Tilt Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to gravity tilt trailer options: deck width, wood vs steel decks, GVWR and winch options. Learn what each choice does and when it matters.',
    image: "/images/social/gravity-tilt.jpg",
    type: "article",
  },
  {
    match: "/build-your-power-tilt",
    mode: "exact",
    title: 'Power Tilt Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to power tilt trailer options: deck lengths, GVWR, hydraulics, remotes and winches. Learn what each choice does and when it matters.',
    image: "/images/social/power-tilt.jpg",
    type: "article",
  },
  {
    match: "/build-your-dump-trailer",
    mode: "exact",
    title: 'Dump Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to dump trailer options: GVWR, hoists, wall heights, floors, ramps and tarps. Learn what each choice does and when it matters.',
    image: "/images/social/dump.jpg",
    type: "article",
  },
  {
    match: "/build-your-car-hauler",
    mode: "exact",
    title: 'Car Hauler Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to car hauler options: 2- to 5-car layouts, wedge vs stinger, GVWR and tow vehicles. Learn what each choice does and when it matters.',
    image: "/images/social/car-hauler.jpg",
    type: "article",
  },
  {
    match: "/build-your-equipment-trailer",
    mode: "exact",
    title: 'Equipment Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to equipment trailer options: bumper pull vs gooseneck, deck width, tilt vs dovetail and axles. Learn what each choice does and when it matters.',
    image: "/images/social/equipment.jpg",
    type: "article",
  },
  {
    match: "/build-your-lowboy-trailer",
    mode: "exact",
    title: 'Lowboy Trailer Options Explained | Juan The Trailer Guy',
    description:
      'Independent guide to lowboy trailer options: axle capacity, deck width and loading options. Learn what each choice does and when it matters.',
    image: "/images/social/lowboy.jpg",
    type: "article",
    // Same guide as the equipment trailer page — canonical points there to
    // avoid duplicate content while keeping this URL accessible.
    canonical: "/build-your-equipment-trailer",
  },

  // --- Standalone blog pages (data-driven articles ship via BlogArticleTemplate) ---
  {
    match: "/dump-trailer-buyers-guide",
    mode: "exact",
    title: "Dump Trailer Buyer's Guide | GVWR, Payload, Sizes & Financing",
    description:
      "Compare dump trailer GVWR, payload, sizes, axles, floors, hoists and towing setups. Learn what to check before you buy, tow or maintain a dump trailer.",
    image: "/__l5e/assets-v1/cddc04d2-af92-4c2c-86d4-171f2273a511/gooseneck-dump-trailer-137906-1600.jpg",
    imageAlt: "16K gooseneck dump trailer with the bed raised in Perris, California",
    type: "article",
  },
  {
    match: "/trailer-financing-buyers-guide",
    mode: "exact",
    title: "Trailer Financing Buyer's Guide | Dump, Equipment & Gooseneck Trailers",
    description:
      "Learn how trailer financing works while comparing GVWR, payload, truck capacity and trailer specs. Understand personal, commercial and rent-to-own options before you talk to any lender.",
    type: "article",
  },
  {
    match: "/blog/non-cdl-hotshot-trailer-weight-guide",
    mode: "exact",
    title: "Non-CDL Hotshot Trailer Guide | Weight, GVWR & Payload",
    description:
      "Building a Non-CDL hotshot setup? Learn how trailer dry weight, GVWR, payload, trailer length and derating work together when choosing the right flatbed trailer.",
    image: "/images/social/non-cdl-hotshot-payload.jpg",
    imageAlt:
      "Non-CDL hotshot flatbed trailer GVWR, dry weight and payload comparison graphic",
    type: "article",
  },
  {
    match: "/blog/mrovi-trailer-parts-northeast-trailer-parts-supplier",
    mode: "exact",
    title: "Inside Mrovi Trailer Parts | Flemington NJ Trailer Parts Warehouse",
    description:
      "Go inside Mrovi Trailer Parts in Flemington, New Jersey — a Northeast trailer parts warehouse stocking trailer axles, Dexter components, brakes, suspension, tires, wheels and dump trailer parts. Open 7 days, shipping to the lower 48.",
    type: "article",
  },
  {
    match: "/blog/235-85r16-14-ply-trailer-tire-guide",
    mode: "exact",
    title: "235/85R16 14-Ply Trailer Tires: Buyer's Guide",
    description:
      "What 235/85R16 14 ply trailer tires are built for. Load Range G explained, sizing breakdown, best applications, a 6-point buying checklist and heavy duty trailer tire tips.",
    type: "article",
  },
  {
    match: "/blog/rent-to-own-vs-trailer-financing",
    mode: "exact",
    title: "Pillar Guide: Rent-to-Own vs Trailer Financing",
    description:
      "The complete trailer financing pillar guide — rent-to-own vs traditional financing, credit, down payment, monthly cost, approval speed, and best fit by business.",
    type: "article",
  },
  {
    match: "/blog/finance-trailer-with-bad-credit",
    mode: "exact",
    title: "Can I Finance a Trailer with Bad Credit? | Juan The Trailer Guy",
    description: "Yes — real trailer financing and lease-to-own programs that approve buyers with sub-600 credit. Docs needed and how to improve approval odds.",
    type: "article",
  },
  {
    match: "/blog/credit-score-for-trailer-financing",
    mode: "exact",
    title: "What Credit Score Do I Need to Finance a Trailer?",
    description: "Minimum FICO scores for trailer financing by lender and program — Synchrony, Beacon Hill, and lease-to-own.",
    type: "article",
  },
  {
    match: "/blog/rent-to-own-trailer-requirements",
    mode: "exact",
    title: "Rent-to-Own Trailer Requirements | Juan The Trailer Guy",
    description: "Exact documents, income, and credit qualifications for lease-to-own trailer programs like C3 Rentals.",
    type: "article",
  },
  {
    match: "/blog/commercial-trailer-financing-explained",
    mode: "exact",
    title: "Commercial Trailer Financing Explained | Juan The Trailer Guy",
    description: "How commercial equipment financing works for trailer buyers — terms, Section 179 tax benefits, and underwriting criteria.",
    type: "article",
  },
  {
    match: "/blog/best-financing-for-new-business-owners",
    mode: "exact",
    title: "Best Trailer Financing for New Business Owners",
    description: "Trailer financing options built for startups and under-2-year businesses — lease-to-own, personal, and startup-friendly commercial.",
    type: "article",
  },
  {
    match: "/blog/how-to-start-a-hotshot-hauling-business",
    mode: "exact",
    title: "How to Start a Hotshot Hauling Business | Juan The Trailer Guy",
    description: "Step-by-step guide to launching a hotshot trucking business — truck and trailer specs, CDL rules, MC authority, and how to find paying loads.",
    type: "article",
  },
  {
    match: "/blog/dump-trailer-guide",
    mode: "exact",
    title: "The Complete Dump Trailer Buyer's Guide | Juan The Trailer Guy",
    description:
      "Sizes, GVWR, walls, hoists, and financing — everything you need to know before buying a dump trailer.",
    type: "article",
  },
  {
    match: "/blog/14k-vs-16k-dump-trailer",
    mode: "exact",
    title: "14K vs 16K Dump Trailer — Which One Should You Buy?",
    description:
      "Side-by-side comparison of 14K and 16K dump trailers covering payload, CDL implications, and real-world use.",
    type: "article",
  },
  {
    match: "/blog/how-to-choose-a-dump-trailer",
    mode: "exact",
    title: "How to Choose the Right Dump Trailer | Juan The Trailer Guy",
    description:
      "Match the right dump trailer to your work — capacity, length, side height, gates, and daily-use features.",
    type: "article",
  },
  {
    match: "/blog/roll-off-vs-dump-trailer",
    mode: "exact",
    title: "Roll-Off Trailer vs Dump Trailer — Which Fits Your Business?",
    description:
      "Compare roll-off and dump trailer setups for contractors, junk haulers, and demo crews.",
    type: "article",
  },
  {
    match: "/blog/dump-trailer-vs-roll-off-trailer",
    mode: "exact",
    title: "Dump Trailer vs Roll-Off Trailer — Which Should You Buy?",
    description:
      "Pros, cons, and use cases for dump vs roll-off trailers so you can pick the right tool for the job.",
    type: "article",
  },
  {
    match: "/blog/gravity-tilt-vs-power-tilt",
    mode: "exact",
    title: "Gravity Tilt vs Power Tilt Trailers — How to Choose",
    description:
      "Compare gravity tilt and power tilt decks for loading speed, control, and equipment compatibility.",
    type: "article",
  },
  {
    match: "/blog/best-dump-trailer-for-contractors",
    mode: "exact",
    title: "Best Dump Trailer for Contractors | Juan The Trailer Guy",
    description:
      "The dump trailer setups working contractors choose — and the features that matter on real jobsites.",
    type: "article",
  },
  {
    match: "/blog/what-is-a-lowboy-trailer",
    mode: "exact",
    title: "What Is a Lowboy Trailer? | Juan The Trailer Guy",
    description:
      "Lowboy trailers explained — deck height, axle capacity, loading options, and how they differ from equipment trailers.",
    type: "article",
  },
  {
    match: "/blog/lowboy-trailer-deck-width",
    mode: "exact",
    title: "Lowboy Trailer Deck Width — Narrow vs Wide",
    description:
      "Pick the right lowboy deck width for your equipment, ramps, and roadworthy hauling.",
    type: "article",
  },
  {
    match: "/blog/lowboy-trailer-axle-capacity",
    mode: "exact",
    title: "Lowboy Trailer Axle Capacity Guide",
    description:
      "How axle ratings, configurations, and brake setup affect a lowboy's real-world capacity.",
    type: "article",
  },
  {
    match: "/blog/lowboy-trailer-rails-vs-open-deck",
    mode: "exact",
    title: "Lowboy Rails vs Open Deck — Pros and Cons",
    description:
      "When rails help, when open deck wins, and how to spec a lowboy for the loads you actually haul.",
    type: "article",
  },
  {
    match: "/blog/gooseneck-vs-bumper-pull-lowboy",
    mode: "exact",
    title: "Gooseneck vs Bumper Pull Lowboy Trailers",
    description:
      "Compare gooseneck and bumper pull lowboy hitch setups for capacity, control, and daily use.",
    type: "article",
  },
  {
    match: "/blog/best-trailer-for-skid-steer",
    mode: "exact",
    title: "Best Trailer for a Skid Steer | Juan The Trailer Guy",
    description:
      "Match the right deck length, axle capacity, and loading style to your skid steer.",
    type: "article",
  },
  {
    match: "/blog/best-trailer-for-mini-excavator",
    mode: "exact",
    title: "Best Trailer for a Mini Excavator | Juan The Trailer Guy",
    description:
      "Pick the right trailer for a mini excavator — capacity, deck width, ramps, and tie-downs.",
    type: "article",
  },
  {
    match: "/blog/lowboy-trailer-guides",
    mode: "exact",
    title: "Lowboy Trailer Buying Guides | Juan The Trailer Guy",
    description:
      "Hub of lowboy trailer guides — axles, deck width, rails, hitch style, loading, and capacity.",
    type: "website",
  },
  {
    match: "/blog/lowboy-trailers-explained",
    mode: "exact",
    title: "Lowboy Trailers Explained | Juan The Trailer Guy",
    description:
      "A plain-English explainer on lowboy trailers — what they are, who uses them, and how to spec one.",
    type: "article",
  },
  {
    match: "/blog/lowboy-vs-equipment",
    mode: "exact",
    title: "Lowboy vs Equipment Trailer — What's the Difference?",
    description:
      "Side-by-side: lowboy vs equipment trailer for capacity, deck height, and loading options.",
    type: "article",
  },
  {
    match: "/blog/lowboy-trailer-size",
    mode: "exact",
    title: "Lowboy Trailer Size Guide | Juan The Trailer Guy",
    description:
      "Pick the right lowboy length and deck size for your equipment and trucking setup.",
    type: "article",
  },
  {
    match: "/blog/lowboy-weight-capacity",
    mode: "exact",
    title: "Lowboy Trailer Weight Capacity Explained",
    description:
      "How GVWR, axle ratings, and braking determine the real working capacity of a lowboy trailer.",
    type: "article",
  },
  {
    match: "/blog/lowboy-ramps-vs-tilt",
    mode: "exact",
    title: "Lowboy Ramps vs Tilt Deck — Pros and Cons",
    description:
      "Ramps or tilt deck on a lowboy? Compare loading speed, safety, and equipment compatibility.",
    type: "article",
  },
  {
    match: "/blog/roll-off-trailers-explained",
    mode: "exact",
    title: "Roll-Off Trailers Explained | Juan The Trailer Guy",
    description:
      "Roll-off trailers explained — hooklift vs cable, rail widths, container compatibility, and use cases.",
    type: "article",
  },
  {
    match: "/blog/roll-off-rail-width",
    mode: "exact",
    title: "Roll-Off Rail Width — How to Pick the Right Spec",
    description:
      "Rail width determines container compatibility. Here's how to pick a roll-off that fits your cans.",
    type: "article",
  },
  {
    match: "/blog/roll-off-cdl",
    mode: "exact",
    title: "Roll-Off Trailers & CDL Requirements",
    description:
      "When a roll-off trailer requires a CDL — and how to spec one that keeps you non-CDL.",
    type: "article",
  },
  {
    match: "/blog/roll-off-derating",
    mode: "exact",
    title: "Roll-Off Trailer Derating Explained",
    description:
      "What derating is, when to do it, and how it affects your roll-off trailer's legal capacity.",
    type: "article",
  },
  {
    match: "/blog/roll-off-size",
    mode: "exact",
    title: "Roll-Off Trailer Size Guide",
    description:
      "Pick the right roll-off trailer size for the containers and routes you run.",
    type: "article",
  },
  {
    match: "/blog/roll-off-residential-vs-commercial",
    mode: "exact",
    title: "Roll-Off: Residential vs Commercial Setups",
    description:
      "Compare residential and commercial roll-off setups — container size, route patterns, and equipment choice.",
    type: "article",
  },
  {
    match: "/blog/how-to-compare-trailers",
    mode: "exact",
    title: "How to Compare Trailers Before You Buy",
    description:
      "A framework for comparing trailers across capacity, build quality, financing, and total cost of ownership.",
    type: "article",
  },
  {
    match: "/blog/non-cdl-vs-cdl-40-flatbed",
    mode: "exact",
    title: "Non-CDL vs CDL 40 ft Flatbed — Which to Buy?",
    description:
      "Compare non-CDL and CDL-rated 40 ft flatbed setups for hauling capacity and legal compliance.",
    type: "article",
  },
  {
    match: "/blog/trailer-maintenance-tips",
    mode: "exact",
    title: "Trailer Maintenance Tips That Save You Money",
    description:
      "Simple maintenance routines that extend trailer life and keep your equipment on the road.",
    type: "article",
  },

  // --- Catch-all prefixes for dynamic / data-driven blog and city pages.
  //     These set sane fallbacks; the actual page component overrides with
  //     article- or city-specific values via setPageMeta in useEffect. ---
  {
    match: "/blog/",
    mode: "prefix",
    title: "Blog | Juan The Trailer Guy",
    description:
      "Trailer guides, comparisons, and how-tos from Juan The Trailer Guy.",
    type: "article",
  },
];

const toAbsoluteImage = (img?: string): string =>
  img
    ? /^https?:\/\//i.test(img)
      ? img
      : `${SITE_ORIGIN}${img.startsWith("/") ? "" : "/"}${img}`
    : `${SITE_ORIGIN}${DEFAULT_IMAGE}`;

/**
 * Apply registry-based SEO for the given pathname. Page-level setPageMeta
 * calls run after this and may override these defaults.
 */
export const applyRouteSeo = (pathname: string): void => {
  const entry =
    ROUTE_SEO.find((e) => (e.mode ?? "exact") === "exact" && e.match === pathname) ??
    ROUTE_SEO.find((e) => e.mode === "prefix" && pathname.startsWith(e.match));

  if (!entry) {
    // Unknown route — at minimum point canonical/og:url at the right origin.
    setPageMeta(
      document.title || "Juan The Trailer Guy",
      undefined,
      pathname,
      { image: DEFAULT_IMAGE, type: "website" },
    );
    return;
  }

  setPageMeta(entry.title, entry.description, entry.canonical ?? pathname, {
    image: toAbsoluteImage(entry.image),
    type: entry.type ?? "website",
    imageAlt: entry.imageAlt,
  });
};